import { useStickyClass } from '../hooks/useStickyClass'

export interface ScrollToTopProps {
  /** How far down the page the button appears, in pixels. */
  offset?: number
  label?: string
}

/**
 * The back-to-top button, fixed to the bottom corner on every viewport.
 *
 * Visibility reuses `useStickyClass` — it is the same question the sticky header asks,
 * "are we past a scroll offset", so it needs no second scroll listener of its own.
 *
 * It stays in the DOM and toggles a class rather than mounting and unmounting, so the
 * fade has something to animate, and it is `aria-hidden` while hidden so the button is
 * not tabbable when it is invisible.
 */
export function ScrollToTop({ offset = 400, label = 'Back to top' }: ScrollToTopProps) {
  const shown = useStickyClass(offset)

  const toTop = () => {
    // `smooth` is honoured everywhere current, and browsers with reduce-motion set
    // respect the user's preference on their own.
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <button
      type="button"
      className={shown ? 'ul-to-top ul-to-top--shown' : 'ul-to-top'}
      onClick={toTop}
      aria-label={label}
      aria-hidden={!shown}
      tabIndex={shown ? 0 : -1}
    >
      {/* The icon font has no plain up arrow — `next` is a right chevron, turned a
          quarter to point up by `.ul-to-top i` in the stylesheet. */}
      <i className="flaticon-next" />
    </button>
  )
}
